import type { ObjectType } from '@/@types/graphql-types';
import type { SubscriptionInfo } from '@/features/jw-pro/types';
import type VueRouter from 'vue-router';
import type { Route } from 'vue-router';
import type { LastVistedTitleDetails } from '@/stores/pinia/useTitleDetailsStore';

import { SUBSCRIPTIONS } from '@/features/jw-pro/constants';

import { ProModal } from '@/lazy-load-components';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { VFMHelper } from '@/helpers/vfm/vfm-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowTitleContext } from '@/helpers/tracking/providers';

import { useUserStore } from '@/stores/pinia/useUserStore';

// EXP_JW_PLUS_FREE_TRIAL
import { jwtvFreeTrialExpSuccessHref, readFreeTrialContext } from '@/components/experiments/JWTVFreeTrialExp/helper';
// EXP_JW_PLUS_FREE_TRIAL

// EXP_JW_PRO_PRICING_V2
import { useUserAgent } from '@/helpers/composables/useUserAgent';
import { getTrackingDeviceValue } from '@/components/experiments/JwProPricingExp/helper';
import { useExperiment } from '@/libraries/experiment';
// EXP_JW_PRO_PRICING_V2

export function findSubscriptionBy(key: keyof SubscriptionInfo, value: string | null) {
	return Object.values(SUBSCRIPTIONS).find(subscription => subscription[key] == value) ?? null;
}

const SELECTED_SUBSCRIPTION = 'selected_subscription' as const;

export function saveSelectedSubscriptionPriceId(subscriptionPriceId: string) {
	localStorage.setItem(SELECTED_SUBSCRIPTION, subscriptionPriceId);
}

export function removeSelectedSubscriptionPriceId() {
	localStorage.removeItem(SELECTED_SUBSCRIPTION);
}

export function getSelectedSubscriptionPriceId() {
	return localStorage.getItem(SELECTED_SUBSCRIPTION);
}

const ACTIVE_TITLE_FULL_PATH = 'active_title_full_path' as const;

export function saveActiveTitleFullPath(titleFullPath: string) {
	localStorage.setItem(ACTIVE_TITLE_FULL_PATH, titleFullPath);
}

export function removeActiveTitleFullPath() {
	localStorage.removeItem(ACTIVE_TITLE_FULL_PATH);
}

export function getActiveTitleFullPath() {
	return localStorage.getItem(ACTIVE_TITLE_FULL_PATH);
}

export function priceFormatter(locale: string, price: number, currency: string = 'EUR'): string {
	const formattedLocale = locale.replace(/_/g, '-');

	if (currency === 'AUD' || currency === 'CAD') {
		// For AUD and CAD, we need to use the locale of the country
		const numberPart = new Intl.NumberFormat(formattedLocale, {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(price);
		if (currency === 'AUD') {
			return `A$ ${numberPart}`;
		} else if (currency === 'CAD') {
			return `CA$ ${numberPart}`;
		}
	}

	return new Intl.NumberFormat(formattedLocale, { style: 'currency', currency: currency }).format(price);
}

const LAST_PRO_VIEW = 'last_pro_view';

export function saveLastProView(view: string) {
	localStorage.setItem(LAST_PRO_VIEW, view);
}

export function getLastProView() {
	return localStorage.getItem(LAST_PRO_VIEW);
}

const LAST_VIEWED_TITLE = 'last_viewed_title';

interface StoredLastTitleDetails {
	titleObjectId: number;
	titleObjectType: ObjectType;
}

export function saveLastViewedTitleDetail({ titleObjectId, titleObjectType }: LastVistedTitleDetails) {
	if (titleObjectId == null || titleObjectType == null) return;

	const titleInfo: StoredLastTitleDetails = { titleObjectId, titleObjectType };

	localStorage.setItem(LAST_VIEWED_TITLE, JSON.stringify(titleInfo));
}

function getLastViewedTitleDetail() {
	return JSON.parse(localStorage.getItem(LAST_VIEWED_TITLE) ?? 'null') as StoredLastTitleDetails | null;
}

function removeLastViewedTitleDetail() {
	localStorage.removeItem(LAST_VIEWED_TITLE);
}

export function handleStripeRedirect(
	route: Route,
	router: VueRouter,
	jwLoginId: string | undefined,
	skipRedirect = false
) {
	if (route.query['jw-pro'] === 'redirect') {
		if (!skipRedirect) {
			router.push({ name: 'app.titles.popular.list' });
		}

		ModalHelper.openModal(
			ProModal,
			{ jwtvFreeTrialExpContext: readFreeTrialContext() ?? undefined },
			{ cssClass: 'pro-jw-pro-modal' },
			'StripeReturn',
			true,
			[],
			() => {
				router.replace({
					query: {
						'jw-pro': undefined,
					},
				});
			}
		);

		saveLastProView('StripeReturn');
	} else if (route.query['jw-pro'] === 'success') {
		const sessionId = route.query['session-id'];

		if (!skipRedirect) {
			router.push({ name: 'app.titles.popular.list' });
		}

		ModalHelper.openModal(ProModal, { modalType: 'loading' }, undefined, undefined, true);
		if (typeof sessionId === 'string' && jwLoginId) {
			handleStripe(jwLoginId, router);
		} else {
			console.error('missing or multiple session-id query params after Stripe redirect');
		}
	}
}

async function handleStripe(jwLoginId: string, router: VueRouter) {
	const contexts: SnowplowTitleContext[] = [];
	const title = getLastViewedTitleDetail();
	if (title != null) {
		contexts.push(new SnowplowTitleContext(title.titleObjectId, title.titleObjectType));
	}

	setTimeout(async () => {
		const userStore = useUserStore();
		await userStore.updateUserPremiumStatus();
		const isPremium = userStore.isPremium;

		if (isPremium) {
			await VFMHelper.close();
			ModalHelper.openModal(ProModal, { modalType: 'success' }, undefined, undefined, true);

			TrackingHelper.trackEvent(
				'user_account',
				{
					action: 'subscription_signup',
					label: jwLoginId,
					property: getLastProView() || undefined,
				},
				contexts
			);

			// EXP_JW_PRO_PRICING_V2
			const { experimentInfo: pricingExpInfo } = useExperiment('EXP_JW_PRO_PRICING_V2');
			if (pricingExpInfo.value?.variant) {
				const { deviceType } = useUserAgent();
				TrackingHelper.trackEvent('onpage_test', {
					action: 'click',
					label: 'purchased_pro',
					value: getTrackingDeviceValue(deviceType.value),
					// action is 'click' for reporting, but this isn't a real click — still, nonInteraction
					// stays false (not true, the way a genuine impression would be) since this pageview
					// represents a real completed purchase, not passive/returning-visitor traffic.
					nonInteraction: false,
				});
			}
			// EXP_JW_PRO_PRICING_V2

			setTimeout(() => {
				const url = new URL(window.location.href);
				if (url.searchParams.get('jw-pro')) {
					url.searchParams.delete('jw-pro');
				}
				removeLastViewedTitleDetail();
				// EXP_JW_PLUS_FREE_TRIAL: null unless a free trial checkout was started
				window.location.href = jwtvFreeTrialExpSuccessHref(router) ?? url.toString();
			}, 3000);
		} else {
			await VFMHelper.close();
			ModalHelper.openModal(ProModal, { modalType: 'error' }, undefined, undefined, true);

			TrackingHelper.trackEvent('user_account', {
				action: 'subscription_signup_error',
				label: jwLoginId,
				property: getLastProView() || undefined,
			});
		}
	}, 3000);
}
